'use client';

import React, { useMemo, useState } from 'react';
import type { PaperPosition } from '@/app/paper-trading/utils/paperTradingStorage';
import type { StoredTrade } from '@/app/utils/tradeStorage';

// ─── Sector mapping (common tickers → sector) ────────────────────────────────
const SECTOR_MAP: Record<string, string> = {
  // Tech
  AAPL: 'Tech', MSFT: 'Tech', GOOGL: 'Tech', GOOG: 'Tech', META: 'Tech',
  NVDA: 'Tech', AMD: 'Tech', INTC: 'Tech', TSLA: 'Tech', AMZN: 'Tech',
  NFLX: 'Tech', CRM: 'Tech', ADBE: 'Tech', ORCL: 'Tech', QCOM: 'Tech',
  AVGO: 'Tech', TXN: 'Tech', MU: 'Tech', AMAT: 'Tech', LRCX: 'Tech',
  // Finance
  JPM: 'Finance', BAC: 'Finance', GS: 'Finance', MS: 'Finance', WFC: 'Finance',
  C: 'Finance', BLK: 'Finance', AXP: 'Finance', V: 'Finance', MA: 'Finance',
  PYPL: 'Finance', SQ: 'Finance', COIN: 'Finance',
  // Healthcare
  JNJ: 'Healthcare', PFE: 'Healthcare', MRNA: 'Healthcare', ABBV: 'Healthcare',
  UNH: 'Healthcare', CVS: 'Healthcare', LLY: 'Healthcare', BMY: 'Healthcare',
  // Energy
  XOM: 'Energy', CVX: 'Energy', COP: 'Energy', SLB: 'Energy', OXY: 'Energy',
  // Consumer
  WMT: 'Consumer', COST: 'Consumer', TGT: 'Consumer', HD: 'Consumer', MCD: 'Consumer',
  SBUX: 'Consumer', NKE: 'Consumer', DIS: 'Consumer',
  // Industrials
  BA: 'Industrials', CAT: 'Industrials', GE: 'Industrials', MMM: 'Industrials',
  // Crypto (ETFs/proxies)
  BITO: 'Crypto', GBTC: 'Crypto', ETHE: 'Crypto',
  // ETFs
  SPY: 'ETF', QQQ: 'ETF', IWM: 'ETF', DIA: 'ETF', GLD: 'ETF', TLT: 'ETF',
};

function getSector(symbol: string): string {
  return SECTOR_MAP[symbol.toUpperCase()] ?? 'Other';
}

const SECTOR_COLORS: Record<string, string> = {
  Tech: '#6366f1',
  Finance: '#f59e0b',
  Healthcare: '#10b981',
  Energy: '#f97316',
  Consumer: '#ec4899',
  Industrials: '#8b5cf6',
  Crypto: '#06b6d4',
  ETF: '#64748b',
  Other: '#94a3b8',
};

interface PositionHeatMapProps {
  positions: PaperPosition[];
  trades: StoredTrade[];
}

interface PositionCell {
  symbol: string;
  sector: string;
  direction: 'long' | 'short';
  entryPrice: number;
  currentPrice: number;
  quantity: number;
  positionValue: number;
  pnl: number;
  pnlPct: number;
  leverage: number;
  stopLoss: number | null;
  drawdownFromEntry: number; // % drop from entry (negative = loss)
  stopDistance: number | null; // % from current to stop
  riskExposure: 'high' | 'medium' | 'low';
}

function calcRiskExposure(pnlPct: number, stopDistance: number | null): 'high' | 'medium' | 'low' {
  if (pnlPct <= -3 || (stopDistance !== null && stopDistance <= 1)) return 'high';
  if (pnlPct <= -1.5 || (stopDistance !== null && stopDistance <= 2.5)) return 'medium';
  return 'low';
}

export default function PositionHeatMap({ positions, trades }: PositionHeatMapProps) {
  const [activeTab, setActiveTab] = useState<'positions' | 'trades'>('positions');
  const [hoveredSymbol, setHoveredSymbol] = useState<string | null>(null);

  // ─── Build position cells from open paper positions ──────────────────────
  const positionCells = useMemo((): PositionCell[] => {
    return positions.map((pos) => {
      const pnl = pos.direction === 'long'
        ? (pos.currentPrice - pos.entryPrice) * pos.quantity
        : (pos.entryPrice - pos.currentPrice) * pos.quantity;
      const pnlPct = pos.direction === 'long'
        ? ((pos.currentPrice - pos.entryPrice) / pos.entryPrice) * 100
        : ((pos.entryPrice - pos.currentPrice) / pos.entryPrice) * 100;
      const positionValue = pos.entryPrice * pos.quantity;
      const stopDistance = pos.stopLoss
        ? pos.direction === 'long'
          ? ((pos.currentPrice - pos.stopLoss) / pos.currentPrice) * 100
          : ((pos.stopLoss - pos.currentPrice) / pos.currentPrice) * 100
        : null;
      return {
        symbol: pos.symbol.toUpperCase(),
        sector: getSector(pos.symbol),
        direction: pos.direction,
        entryPrice: pos.entryPrice,
        currentPrice: pos.currentPrice,
        quantity: pos.quantity,
        positionValue,
        pnl,
        pnlPct,
        leverage: 1,
        stopLoss: pos.stopLoss,
        drawdownFromEntry: pnlPct,
        stopDistance,
        riskExposure: calcRiskExposure(pnlPct, stopDistance),
      };
    });
  }, [positions]);

  // ─── Build trade-based cells (recent open-like exposure from trade log) ──
  const tradeCells = useMemo((): PositionCell[] => {
    // Group by symbol, compute aggregate exposure
    const map: Record<string, { pnl: number; count: number; totalSize: number; wins: number }> = {};
    trades.forEach((t) => {
      const sym = t.symbol?.toUpperCase();
      if (!sym) return;
      if (!map[sym]) map[sym] = { pnl: 0, count: 0, totalSize: 0, wins: 0 };
      map[sym].pnl += t.pnl;
      map[sym].count++;
      map[sym].totalSize += Math.abs(t.size ?? 0);
      if (t.pnl > 0) map[sym].wins++;
    });
    return Object.entries(map)
      .sort((a, b) => Math.abs(b[1].pnl) - Math.abs(a[1].pnl))
      .slice(0, 20)
      .map(([symbol, d]) => {
        const pnlPct = d.totalSize > 0 ? (d.pnl / d.totalSize) * 100 : 0;
        return {
          symbol,
          sector: getSector(symbol),
          direction: d.pnl >= 0 ? 'long' : 'short',
          entryPrice: 0,
          currentPrice: 0,
          quantity: d.count,
          positionValue: d.totalSize,
          pnl: d.pnl,
          pnlPct,
          leverage: 1,
          stopLoss: null,
          drawdownFromEntry: pnlPct,
          stopDistance: null,
          riskExposure: calcRiskExposure(pnlPct, null),
        };
      });
  }, [trades]);

  const cells = activeTab === 'positions' ? positionCells : tradeCells;

  // ─── Sector grouping ─────────────────────────────────────────────────────
  const sectorGroups = useMemo(() => {
    const groups: Record<string, PositionCell[]> = {};
    cells.forEach((c) => {
      if (!groups[c.sector]) groups[c.sector] = [];
      groups[c.sector].push(c);
    });
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length);
  }, [cells]);

  // ─── Summary stats ───────────────────────────────────────────────────────
  const summary = useMemo(() => {
    if (!cells.length) return null;
    const totalValue = cells.reduce((s, c) => s + Math.abs(c.positionValue), 0);
    const totalPnl = cells.reduce((s, c) => s + c.pnl, 0);
    const highRisk = cells.filter((c) => c.riskExposure === 'high').length;
    const sectors = new Set(cells.map((c) => c.sector)).size;
    return { totalValue, totalPnl, highRisk, sectors, count: cells.length };
  }, [cells]);

  const hovered = hoveredSymbol ? cells.find((c) => c.symbol === hoveredSymbol) : null;

  function cellBg(cell: PositionCell): string {
    const p = cell.pnlPct;
    if (p >= 5) return 'bg-emerald-500/70 border-emerald-400/40';
    if (p >= 2) return 'bg-emerald-500/40 border-emerald-500/30';
    if (p >= 0.5) return 'bg-emerald-500/20 border-emerald-500/20';
    if (p >= -0.5) return 'bg-muted/30 border-border';
    if (p >= -2) return 'bg-red-500/20 border-red-500/20';
    if (p >= -5) return 'bg-red-500/40 border-red-500/30';
    return 'bg-red-500/70 border-red-400/40';
  }

  const isEmpty = cells.length === 0;

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Position Heat Map</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Sector/symbol overlap, leverage risk & drawdown exposure</p>
        </div>
        <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-0.5">
          {(['positions', 'trades'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`text-xs px-2.5 py-1 rounded-md transition-colors capitalize ${activeTab === tab ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {tab === 'positions' ? 'Open Positions' : 'Trade Log'}
            </button>
          ))}
        </div>
      </div>

      {/* Summary row */}
      {summary && (
        <div className="grid grid-cols-4 gap-2 mb-4">
          {[
            { label: 'Symbols', value: String(summary.count) },
            { label: 'Sectors', value: String(summary.sectors) },
            { label: 'Total P&L', value: `${summary.totalPnl >= 0 ? '+' : ''}$${Math.round(summary.totalPnl).toLocaleString()}`, colored: true, pos: summary.totalPnl >= 0 },
            { label: 'High Risk', value: String(summary.highRisk), colored: true, pos: summary.highRisk === 0 },
          ].map((s) => (
            <div key={s.label} className="bg-muted/20 rounded-lg p-2.5 text-center">
              <p className="text-xs text-muted-foreground">{s.label}</p>
              <p className={`text-sm font-semibold font-mono mt-0.5 ${s.colored ? (s.pos ? 'text-emerald-400' : 'text-red-400') : 'text-foreground'}`}>
                {s.value}
              </p>
            </div>
          ))}
        </div>
      )}

      {isEmpty ? (
        <div className="h-48 flex flex-col items-center justify-center gap-2">
          <p className="text-sm text-muted-foreground">
            {activeTab === 'positions' ? 'No open paper positions' : 'No trades in trade log'}
          </p>
          <p className="text-xs text-muted-foreground/60">
            {activeTab === 'positions' ? 'Execute paper trades to see position heat map' : 'Log trades to see symbol exposure'}
          </p>
        </div>
      ) : (
        <>
          {/* Hover tooltip */}
          {hovered && (
            <div className="mb-3 p-3 bg-muted/30 border border-border rounded-lg grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <p className="text-muted-foreground">Symbol</p>
                <p className="font-semibold text-foreground">{hovered.symbol}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Sector</p>
                <p className="font-semibold" style={{ color: SECTOR_COLORS[hovered.sector] ?? '#94a3b8' }}>{hovered.sector}</p>
              </div>
              <div>
                <p className="text-muted-foreground">P&L</p>
                <p className={`font-semibold font-mono ${hovered.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {hovered.pnl >= 0 ? '+' : ''}${hovered.pnl.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Risk</p>
                <p className={`font-semibold capitalize ${hovered.riskExposure === 'high' ? 'text-red-400' : hovered.riskExposure === 'medium' ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {hovered.riskExposure}
                </p>
              </div>
              {hovered.stopDistance !== null && (
                <div>
                  <p className="text-muted-foreground">Stop Distance</p>
                  <p className="font-semibold font-mono text-amber-400">{hovered.stopDistance.toFixed(2)}%</p>
                </div>
              )}
              {hovered.pnlPct !== 0 && (
                <div>
                  <p className="text-muted-foreground">Drawdown</p>
                  <p className={`font-semibold font-mono ${hovered.pnlPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {hovered.pnlPct >= 0 ? '+' : ''}{hovered.pnlPct.toFixed(2)}%
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Sector groups */}
          <div className="space-y-4">
            {sectorGroups.map(([sector, sectorCells]) => (
              <div key={sector}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: SECTOR_COLORS[sector] ?? '#94a3b8' }} />
                  <span className="text-xs font-semibold text-muted-foreground">{sector}</span>
                  <span className="text-xs text-muted-foreground/60">({sectorCells.length})</span>
                  <div className="flex-1 h-px bg-border" />
                  <span className={`text-xs font-mono font-semibold ${sectorCells.reduce((s, c) => s + c.pnl, 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {sectorCells.reduce((s, c) => s + c.pnl, 0) >= 0 ? '+' : ''}${Math.round(sectorCells.reduce((s, c) => s + c.pnl, 0)).toLocaleString()}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {sectorCells.map((cell) => (
                    <div
                      key={cell.symbol}
                      className={`relative border rounded-lg p-2.5 cursor-default transition-all min-w-[72px] ${cellBg(cell)} ${hoveredSymbol === cell.symbol ? 'ring-2 ring-white/20 scale-105' : 'hover:scale-105'}`}
                      onMouseEnter={() => setHoveredSymbol(cell.symbol)}
                      onMouseLeave={() => setHoveredSymbol(null)}
                    >
                      {/* Risk badge */}
                      {cell.riskExposure === 'high' && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-red-500 ring-1 ring-background" />
                      )}
                      {cell.riskExposure === 'medium' && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400 ring-1 ring-background" />
                      )}
                      <p className="text-xs font-bold text-foreground leading-tight">{cell.symbol}</p>
                      <p className={`text-xs font-mono font-semibold leading-tight mt-0.5 ${cell.pnlPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {cell.pnlPct >= 0 ? '+' : ''}{cell.pnlPct.toFixed(1)}%
                      </p>
                      <p className="text-[10px] text-muted-foreground/70 leading-tight capitalize mt-0.5">
                        {activeTab === 'positions' ? cell.direction : `${cell.quantity}t`}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* P&L scale legend */}
          <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border flex-wrap">
            <span className="text-xs text-muted-foreground">P&L:</span>
            {[
              { label: '≥+5%', cls: 'bg-emerald-500/70' },
              { label: '+2%', cls: 'bg-emerald-500/40' },
              { label: '~0%', cls: 'bg-muted/30' },
              { label: '−2%', cls: 'bg-red-500/40' },
              { label: '≤−5%', cls: 'bg-red-500/70' },
            ].map((l) => (
              <div key={l.label} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded-sm ${l.cls}`} />
                <span className="text-xs text-muted-foreground">{l.label}</span>
              </div>
            ))}
            <div className="ml-2 flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-xs text-muted-foreground">High risk</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="text-xs text-muted-foreground">Med risk</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
